import client from '../../api/client';

function triggerBlobDownload(blob, filename) {
  const objectUrlApi = window.URL || window.webkitURL;
  const safeBlob = blob instanceof Blob
    ? blob
    : new Blob([blob], { type: 'application/pdf' });

  if (!objectUrlApi || typeof objectUrlApi.createObjectURL !== 'function') {
    throw new Error('Download wird von diesem Browser nicht unterstuetzt.');
  }

  const url = objectUrlApi.createObjectURL(safeBlob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);

  try {
    anchor.click();
  } finally {
    anchor.remove();
    objectUrlApi.revokeObjectURL(url);
  }
}

function getFilenameFromDisposition(dispositionHeader) {
  if (!dispositionHeader) {
    return 'Dienstzeitprotokoll.pdf';
  }

  const filenameMatch = dispositionHeader.match(/filename="?([^";]+)"?/i);
  return filenameMatch?.[1] || 'Dienstzeitprotokoll.pdf';
}

async function extractAxiosErrorMessage(error) {
  const fallback = error?.response?.data?.error || error?.message || 'PDF konnte nicht erstellt werden.';
  const responseData = error?.response?.data;

  if (!(responseData instanceof Blob)) {
    return fallback;
  }

  try {
    const text = await responseData.text();
    if (!text) {
      return fallback;
    }

    try {
      const parsed = JSON.parse(text);
      return parsed?.error || fallback;
    } catch {
      return text.slice(0, 300);
    }
  } catch {
    return fallback;
  }
}

function buildDownloadFailureError(message, metadata = {}) {
  const error = new Error(message || 'PDF wurde erstellt, aber der Download ist fehlgeschlagen.');
  Object.entries(metadata).forEach(([key, value]) => {
    error[key] = value;
  });
  return error;
}

async function downloadProtocolPdfResponse(request) {
  try {
    const response = await request();
    const contentType = response.headers?.['content-type'] || '';
    if (!String(contentType).toLowerCase().includes('application/pdf')) {
      const text = response.data instanceof Blob ? await response.data.text() : '';
      throw new Error(text || 'Unerwartete Antwort beim PDF-Export.');
    }

    const filename = getFilenameFromDisposition(response.headers?.['content-disposition']);
    const exportId = response.headers?.['x-pdf-export-id'] || null;
    try {
      triggerBlobDownload(response.data, filename);
    } catch (downloadError) {
      throw buildDownloadFailureError(
        downloadError?.message || 'PDF wurde erstellt, aber der Download konnte nicht gestartet werden.',
        { exportId, filename, isDownloadFailure: true },
      );
    }
    return { exportId, filename };
  } catch (error) {
    if (error?.isDownloadFailure) throw error;
    const message = await extractAxiosErrorMessage(error);
    throw new Error(message);
  }
}

export async function downloadDienstzeitProtokollPdf({
  entryIds,
  protocolPayload,
}) {
  return downloadProtocolPdfResponse(() => client.post(
      '/api/training/export-pdf',
      {
        deliveryChannel: 'download',
        entryIds: Array.isArray(entryIds) ? entryIds : [],
        frontendBaseUrl: typeof window !== 'undefined' ? window.location.origin : '',
        protocolPayload,
      },
      {
        responseType: 'blob',
      },
    ));
}

export async function downloadDienstzeitProtokollForEntry(entryId) {
  return downloadProtocolPdfResponse(() => client.post(
    `/api/training/${entryId}/export-protocol-pdf`,
    {
      deliveryChannel: 'download',
      frontendBaseUrl: typeof window !== 'undefined' ? window.location.origin : '',
    },
    { responseType: 'blob' },
  ));
}

export async function markDienstzeitProtokollCompleted({ exportId, completionNote }) {
  if (!exportId) {
    throw new Error('Missing export id');
  }

  const response = await client.post(`/api/training/export-pdf/${exportId}/complete`, {
    completionNote: completionNote || null,
  });

  return response.data;
}

export async function redownloadDienstzeitProtokollPdf(exportId) {
  if (!exportId) {
    throw new Error('Missing export id');
  }

  const response = await client.get(`/api/training/export-pdf/${exportId}/download`, {
    responseType: 'blob',
  });

  const filename = getFilenameFromDisposition(response.headers?.['content-disposition']);
  triggerBlobDownload(response.data, filename);
  return { filename };
}
